import { useRestaurantStore } from '@/stores/restaurant.store';
import { useRestaurant } from './use-restaurant';
import { useSocket } from './use-socket';
import { computed, onMounted, onUnmounted, ref, toRef, watch } from 'vue';
import { usePrinting } from './use-printing';
import OrdersService from '@/api/services/orders.service';
import { useOrdersStore } from '@/stores/orders.store';
import { useUi } from './use-ui';
import { useModal } from './use-modal';
import ModalPosViewOrder from '@/components/modals/pos/view-order/ModalPosViewOrder.vue';
import { IOrder } from '@/interfaces/IOrder';
import ModalPosNewOrder from '@/components/modals/pos/new-order/ModalPosNewOrder.vue';
import { useSound } from '@vueuse/sound';
import { Globals } from '@/modules/Globals';
import { useDebounceFn, useIntervalFn, useLocalStorage } from '@vueuse/core';
import { PrintPluginStatus } from '@/modules/PrintService';
import { TicketPrintSetting } from '@/enums/PrintEnum';
import { ICall } from '@/interfaces/ICall';
import CallsService from '@/api/services/calls.service';
import { useRoute, useRouter } from 'vue-router/composables';
import { AlertType } from '@/interfaces/IAlert';
import { getServerErrorMessage } from '@/utils/error.utils';
import {
  isNewOrder,
  getPaidOrderCancelDeclineGuardError,
  getOnlineAcceptCajaGuardError,
  isPendingOrder,
} from '@/utils/orders.utils';
import { OrderStatus } from '@/enums/OrderEnums';
import { i18n } from '@/plugins/i18n';
import { PosV2Error } from '@/domain/pos/pos-error';
import { usePosV2 } from './use-pos-v2';
import { usePosNavigation } from './use-pos-navigation';
import { usePosDraftsStore } from '@/stores/pos-drafts.store';
import { usePrintedStore } from '@/stores/printed.store';
import { usePrintHealthStore } from '@/stores/print-health.store';
import { usePrintHealth } from '@/composition/printing/use-print-health';
import { diffRecoveredPrinterKeys } from '@/lib/print/print-health.utils';
import { computePrintCatchUpWindowMinutes } from '@/utils/print-catchup.utils';

export function useOrders(main = false) {
  const socketHelper = useSocket();
  const restaurantStore = useRestaurantStore();
  const printingHelper = usePrinting();
  const ordersService = new OrdersService();
  const store = useOrdersStore();
  const ui = useUi();
  const modals: ReturnType<typeof useModal> = useModal();
  const route = useRoute();
  const router = useRouter();
  const { isPosV2Active } = usePosV2();
  const posNavigation = usePosNavigation();
  const posDrafts = usePosDraftsStore();
  const printedStore = usePrintedStore();
  const printHealthStore = usePrintHealthStore();

  if (main) {
    const lastOrderUpdate = ref(Date.now());
    const orderSound = useSound(`${Globals.uploadsPath}/sounds/order.mp3`);
    const callSound = useSound(`${Globals.uploadsPath}/sounds/call.mp3`);
    const orderSoundPlayInterval = useIntervalFn(
      () => orderSound.play(),
      5000,
      { immediate: false, immediateCallback: true }
    );
    const callSoundPlayInterval = useIntervalFn(() => callSound.play(), 2500, {
      immediate: false,
      immediateCallback: true,
    });

    //#region Data
    async function reloadOrders(branchChanged: boolean) {
      if (!restaurantStore.restaurant || !restaurantStore.selectedBranch)
        return;

      console.log('Reloading orders', { branchChanged });
      const lastOrderUpdateValue = lastOrderUpdate.value;

      try {
        lastOrderUpdate.value = Date.now();
        // Orders received via WS after this instant can't be in the snapshot;
        // updateOrders must not remove them (F4).
        const snapshotAt = Date.now();
        const orders = await OrdersService.v2GetOrders({
          onlyPending: true,
          restaurantId: restaurantStore.restaurant.id!,
          branchId: restaurantStore.selectedBranch.id!,
          dataOptions: {
            itemsPerPage: 1000,
            mustSort: true,
            sortBy: ['createdAt'],
            sortDesc: [false],
          },
        });

        if (branchChanged) {
          store.setOrders(orders.items);
        } else {
          store.updateOrders(orders.items, { snapshotAt });
        }
      } catch (err) {
        ui.serverError(err, 'Error al cargar pedidos');
        lastOrderUpdate.value = lastOrderUpdateValue;
      }
    }

    async function reloadCalls() {
      if (!restaurantStore.restaurant || !restaurantStore.selectedBranch)
        return;

      console.log('Reloading calls');

      try {
        const calls = await CallsService.getAll(
          restaurantStore.restaurant.id!,
          restaurantStore.selectedBranch.id!
        );

        store.setCalls(calls);
      } catch (err) {
        ui.serverError(err, 'Error al cargar llamadas');
      }
    }
    //#endregion

    //#region Print catch-up reconciliation
    /**
     * Orders created/finished on other devices while this PC (or its printing
     * plugin) was offline never fire live WS events here, so their auto-print
     * decision would be lost. reconcilePrints() re-runs the state-based
     * evaluator over recently-updated orders; per-item printed counters make
     * the replay idempotent.
     */
    const lastPrintSyncAt = useLocalStorage('last-print-sync-at', 0);
    let reconcilingPrints = false;
    /**
     * A reconcile request that arrives while a pass is running must not be
     * silently dropped (the pass may have already fetched BEFORE the event
     * that triggered the request): queue exactly one re-run.
     */
    let reconcileAgainRequested = false;
    /**
     * Epoch value of the last reconcile pass that completed with no
     * retryable auto-print failure. While the live epoch differs, the sync
     * point is frozen (heartbeat included) so the fetch window keeps growing
     * until a clean pass re-covers the failed orders.
     */
    let clearedFailureEpoch =
      printingHelper.autoPrintRetryableFailureEpoch.value;

    const hasAnyAutoPrintTrigger = computed(() =>
      [
        printingHelper.ticketPrintingSettings.value,
        printingHelper.comandaPrintingSettings.value,
        printingHelper.stickerPrintingSettings.value,
      ].some((settings) => settings?.trigger !== TicketPrintSetting.Manual)
    );

    const printPipelineReady = computed(
      () =>
        socketHelper.isConnected.value &&
        !!restaurantStore.selectedBranch &&
        printingHelper.status.value === PrintPluginStatus.Connected &&
        !!printingHelper.printers.value?.length &&
        hasAnyAutoPrintTrigger.value
    );

    async function reconcilePrints() {
      if (reconcilingPrints) {
        reconcileAgainRequested = true;
        return;
      }
      if (!printPipelineReady.value) return;

      const restaurantId = restaurantStore.restaurant?.id;
      const branchId = restaurantStore.selectedBranch?.id;
      if (!restaurantId || !branchId) return;

      reconcilingPrints = true;
      try {
        const startedAt = Date.now();
        const epochAtStart =
          printingHelper.autoPrintRetryableFailureEpoch.value;
        const windowMinutes = computePrintCatchUpWindowMinutes({
          lastPrintSyncAt: lastPrintSyncAt.value,
          now: startedAt,
        });

        console.log('[PrintCatchUp] Reconciling prints', { windowMinutes });

        // Print-only fetch: deliberately NOT fed into the orders store (the
        // widened result contains finished/archived orders that don't belong
        // in the pending set).
        const orders = await OrdersService.v2GetOrders({
          onlyPending: true,
          updatedSinceMinutes: windowMinutes,
          restaurantId,
          branchId,
          dataOptions: {
            itemsPerPage: 1000,
            mustSort: true,
            sortBy: ['createdAt'],
            sortDesc: [false],
          },
        });

        // Branch switched mid-fetch: discard, the next trigger re-runs.
        if (restaurantStore.selectedBranch?.id !== branchId) return;

        let printedCount = 0;
        for (const order of orders.items) {
          printedStore.mergeIntoOrder(order);
          const printed = await printingHelper.evaluateOrderAutoPrint(order);
          if (printed) printedCount++;
        }

        // Only advance the sync point after a clean pass: no retryable
        // auto-print failure landed since the pass started, and no live
        // evaluation is still in flight (it could fail after we advance,
        // stranding its order behind the new sync point). Otherwise the
        // window keeps growing (up to the cap in print-catchup.utils.ts)
        // until a later pass re-covers the failed orders.
        const epochNow = printingHelper.autoPrintRetryableFailureEpoch.value;
        if (
          epochNow === epochAtStart &&
          !printingHelper.hasInFlightAutoPrintEvaluations()
        ) {
          lastPrintSyncAt.value = startedAt;
          clearedFailureEpoch = epochNow;
        }

        if (printedCount > 0) {
          ui.showToast(
            AlertType.Info,
            `Se imprimieron ${printedCount} pedidos pendientes`
          );
        }
      } catch (err) {
        console.error('[PrintCatchUp] Error reconciling prints', err);
      } finally {
        reconcilingPrints = false;
        if (reconcileAgainRequested) {
          reconcileAgainRequested = false;
          requestReconcilePrints();
        }
      }
    }

    const requestReconcilePrints = useDebounceFn(() => {
      reconcilePrints().catch((err) => {
        console.error('[PrintCatchUp] Error reconciling prints', err);
      });
    }, 2000);

    // Pipeline-ready transitions: socket reconnect, plugin (re)connect,
    // printers loaded, branch ready — including app start.
    watch(printPipelineReady, (ready, wasReady) => {
      if (ready && !wasReady) requestReconcilePrints();
    });

    // Heartbeat: while the pipeline is ready, live WS events handle prints,
    // so the sync point can keep advancing (covers the browser-closed case).
    // It only advances while no auto-print retry is pending (epoch cleared)
    // and no evaluation is in flight — a retry-safe failure can take longer
    // than one heartbeat period to land, and advancing past the failing
    // order's updated_at would strand it. While frozen, the fetch window
    // keeps growing (up to the 4h cap in print-catchup.utils.ts) so a
    // printer outage never loses orders, only delays them.
    useIntervalFn(() => {
      if (
        printPipelineReady.value &&
        !reconcilingPrints &&
        printingHelper.autoPrintRetryableFailureEpoch.value ===
          clearedFailureEpoch &&
        !printingHelper.hasInFlightAutoPrintEvaluations()
      ) {
        lastPrintSyncAt.value = Date.now();
      }
    }, 60 * 1000);

    // Periodic safety net (no-op unless the pipeline is ready).
    useIntervalFn(() => requestReconcilePrints(), 10 * 60 * 1000);

    // Instant retry on printer recovery (both platforms): the moment an
    // assigned printer transitions offline → online (incl. bridge 'error' →
    // online, e.g. paper refill), reprint the backlog immediately instead of
    // waiting for the 10-min loop. unknown → online never fires (first poll
    // after startup). Keyed on the unified health selector, so it works for
    // bridge and native strategies alike.
    const { assignedPrinterStatusMap } = usePrintHealth();
    watch(assignedPrinterStatusMap, (next, prev) => {
      const recovered = diffRecoveredPrinterKeys(prev ?? {}, next);
      if (!recovered.length) return;
      console.log('[PrintCatchUp] Printer recovered, reconciling', recovered);
      requestReconcilePrints();
    });
    //#endregion

    //#region WS
    let subscribedRestaurantId: number | null = null;
    let subscribedBranchId: number | null = null;

    async function onOrderUpdate(order: IOrder) {
      console.log('Order updated', order);
      //console.log('Order updated', id);
      try {
        // Transient WS-only marker (orders paid together in one payment):
        // strip it BEFORE the store merge so it never leaks into state, and
        // hand it to the auto-print evaluator separately.
        const groupPayment = order.groupPayment;
        delete order.groupPayment;

        if (order.orderItems) {
          order.orderItems = order.orderItems.sort((a, b) =>
            a.index > b.index ? 1 : -1
          );
        }

        const updatedOrAdded = store.updateOrder(order.id, order);

        if (updatedOrAdded) {
          printingHelper.onOrderChange({
            order,
            groupPayment,
          });

          if (!isNewOrder(order) && !isPendingOrder(order)) {
            store.removeOrder(order.id);
          }
        }
      } catch (err) {
        console.error('Error on order update', order, err);
      }
    }

    function showGoToPosToast(message: string) {
      // On any POS page the persistent app-bar alert + sound already cover it.
      if (route.meta?.pos) return;

      ui.showToast(AlertType.Info, message, {
        text: 'Ir a Punto de Venta',
        cb: () => router.push({ name: 'restaurantPos' }),
      });
    }

    async function onNewOrder(order: IOrder) {
      //console.log('New order', id);
      try {
        if (order.orderItems) {
          order.orderItems = order.orderItems.sort((a, b) =>
            a.index > b.index ? 1 : -1
          );
        }

        store.addOrder(order);
        printingHelper.onOrderChange({
          order,
        });
        showGoToPosToast('Nuevo pedido recibido');
      } catch (err) {
        console.error('Error on new order', order, err);
      }
    }

    async function onNewCall(call: ICall) {
      //console.log('New call', call);
      try {
        store.addCall(call);
        showGoToPosToast('Nuevo llamado recibido');
      } catch (err) {
        console.error('Error on new call', call, err);
      }
    }

    /**
     * Another device confirmed the call: remove it locally so this device
     * stops ringing too. The payload carries no table relation — only use the
     * id. removeCall is idempotent, so the confirming device receiving its own
     * broadcast is a no-op.
     */
    function onCallConfirmed(call: ICall) {
      try {
        store.removeCall(call.id);
      } catch (err) {
        console.error('Error on call confirmed', call, err);
      }
    }

    /** Another device marked items printed: max-merge to avoid duplicates. */
    function onOrderPrinted(payload: {
      orderId: number;
      indexIds: number[];
      printT?: number[];
      printC?: number[];
      printE?: number[];
      printedPaidAt?: string | null;
    }) {
      try {
        store.mergePrintedCounters(payload);
      } catch (err) {
        console.error('Error on order printed', payload, err);
      }
    }

    function setSocketCallbacks() {
      if (!restaurantStore.restaurant || !restaurantStore.selectedBranch)
        return;
      console.log(
        'Setting socket callbacks',
        `r:${restaurantStore.restaurant.id}:${restaurantStore.selectedBranch.id}:...`
      );
      socketHelper.socket.value.addEventListener(
        `r:${restaurantStore.restaurant.id}:${restaurantStore.selectedBranch.id}:v2:orderUpdate`,
        onOrderUpdate
      );
      socketHelper.socket.value.addEventListener(
        `r:${restaurantStore.restaurant.id}:${restaurantStore.selectedBranch.id}:v2:order`,
        onNewOrder
      );
      socketHelper.socket.value.addEventListener(
        `r:${restaurantStore.restaurant.id}:${restaurantStore.selectedBranch.id}:v2:call`,
        onNewCall
      );
      socketHelper.socket.value.addEventListener(
        `r:${restaurantStore.restaurant.id}:${restaurantStore.selectedBranch.id}:v2:callConfirm`,
        onCallConfirmed
      );
      socketHelper.socket.value.addEventListener(
        `r:${restaurantStore.restaurant.id}:${restaurantStore.selectedBranch.id}:v2:orderPrinted`,
        onOrderPrinted
      );
      subscribedRestaurantId = restaurantStore.restaurant.id;
      subscribedBranchId = restaurantStore.selectedBranch.id;
    }

    function clearSocketCallbacks() {
      if (!subscribedRestaurantId || !subscribedBranchId) return;

      console.log(
        'Clearing socket callbacks',
        `r:${subscribedRestaurantId}:${subscribedBranchId}:...`
      );
      socketHelper.socket.value.removeEventListener(
        `r:${subscribedRestaurantId}:${subscribedBranchId}:v2:orderUpdate`,
        onOrderUpdate
      );
      socketHelper.socket.value.removeEventListener(
        `r:${subscribedRestaurantId}:${subscribedBranchId}:v2:order`,
        onNewOrder
      );
      socketHelper.socket.value.removeEventListener(
        `r:${subscribedRestaurantId}:${subscribedBranchId}:v2:call`,
        onNewCall
      );
      socketHelper.socket.value.removeEventListener(
        `r:${subscribedRestaurantId}:${subscribedBranchId}:v2:callConfirm`,
        onCallConfirmed
      );
      socketHelper.socket.value.removeEventListener(
        `r:${subscribedRestaurantId}:${subscribedBranchId}:v2:orderPrinted`,
        onOrderPrinted
      );

      // Pending payment groups belong to the branch being left: drop them so
      // no flush runs against the new branch's context (dropped members
      // recover per-order via the catch-up reconciler).
      printingHelper.clearPendingGroupPrints();
    }

    watch(
      () => restaurantStore.selectedBranch,
      (newBranch, oldBranch) => {
        if (newBranch?.id === oldBranch?.id) return;

        if (oldBranch) {
          clearSocketCallbacks();
          store.clear();
          // Widget failure entries / success chip belong to the branch
          // being left.
          printHealthStore.clear();
        }

        if (newBranch) {
          setSocketCallbacks();
        }
      },
      { immediate: true }
    );

    watch(
      [socketHelper.isConnected, () => restaurantStore.selectedBranch],
      ([connected, branch], [oldConnected, oldBranch]) => {
        if (connected && branch) {
          console.log('[RealTime] Connected, reloading orders');
          reloadOrders(branch.id !== oldBranch?.id).catch((err) => {
            console.error('Error reloading orders', err);
          });
          reloadCalls().catch((err) => {
            console.error('Error reloading calls', err);
          });
          // Counters printed while offline must reach the server ASAP so other
          // devices don't duplicate-print.
          printedStore.flushPending().catch((err) => {
            console.error('Error flushing pending printed counters', err);
          });
        }
      }
    );

    watch(
      () => store.unseenOrderIds,
      (unseenOrderIds) => {
        if (unseenOrderIds.length > 0) {
          if (restaurantStore.effectiveTier <= 0) return;

          if (!orderSoundPlayInterval.isActive.value) {
            console.log('Starting order sound');
            orderSoundPlayInterval.resume();
          }
        } else {
          console.log('Stopping order sound');
          orderSoundPlayInterval.pause();
          orderSound.stop();
        }
      },
      { immediate: true, deep: true }
    );

    watch(
      // Keyed on unacknowledged (not raw) calls so the app-bar incoming alert
      // can silence the sound device-locally; a new call re-arms it.
      () => store.unacknowledgedCalls.length,
      (length) => {
        if (length) {
          if (restaurantStore.effectiveTier <= 0) return;

          if (!callSoundPlayInterval.isActive.value) {
            console.log('Starting call sound');
            callSoundPlayInterval.resume();
          }
        } else {
          console.log('Stopping call sound');
          callSoundPlayInterval.pause();
          callSound.stop();
        }
      },
      { immediate: true }
    );

    onUnmounted(() => {
      clearSocketCallbacks();
    });
    //#endregion

    //#region Periodic orders reload
    const reloadOrdersInterval = useIntervalFn(() => {
      const connected = socketHelper.isConnected.value,
        hasBranch = !!restaurantStore.selectedBranch;

      if (!connected || !hasBranch) return;

      // Check if 10 minutes have passed since last update
      const tenMinutes = 10 * 60 * 1000;
      const lastUpdate = lastOrderUpdate.value;
      const now = Date.now();
      const diff = now - lastUpdate;

      if (diff >= tenMinutes) {
        reloadOrders(false).catch((err) => {
          console.error('Error reloading orders', err);
        });
      }
    }, 10000);
    //#endregion
  }

  /**
   * POS V2 branch: opens the order in a workspace tab instead of a modal.
   * Only for POS-context calls — non-POS callers (caja movements, coupon
   * usage) must keep the modal so they aren't navigated away from their page.
   */
  async function openOrderInPosV2Tab(
    orderId: number,
    mode: 'view' | 'edit'
  ): Promise<boolean> {
    store.removeUnseenOrder(orderId);

    try {
      await posDrafts.openOrderTab({ orderId }, mode);
    } catch (error) {
      if (error instanceof PosV2Error) {
        ui.showToast(
          AlertType.Error,
          String(i18n.t(error.i18nKey, error.params))
        );
      } else {
        ui.serverError(error, String(i18n.t('posV2.orderOpenError')));
      }
      return false;
    }

    if (route.name !== 'restaurantPosOrder') {
      posNavigation.rememberOrderWorkspaceOrigin(route.fullPath);
      router.push({ name: 'restaurantPosOrder' }).catch(() => undefined);
    }
    return true;
  }

  /** @returns whether the order view was actually opened (tab or modal). */
  async function openOrder(
    orderOrId: IOrder | number,
    isPosCall: boolean = true
  ): Promise<boolean> {
    if (isPosCall && isPosV2Active.value) {
      const orderId = typeof orderOrId === 'number' ? orderOrId : orderOrId.id;
      return await openOrderInPosV2Tab(orderId, 'view');
    }

    let order: IOrder;

    if (typeof orderOrId === 'number') {
      const orderId = orderOrId;

      if (isPosCall) {
        // Try to get order from store first
        let tries = 0;
        while (tries < 5) {
          order = store.orders?.find?.((o) => o.id === orderId);
          console.log(`Order ${orderId} try ${tries + 1}`, order);

          if (order) {
            break;
          }

          await new Promise((resolve) => setTimeout(resolve, 300));
          tries++;
        }
      } else {
        try {
          order = await OrdersService.v2GetOrderById(
            restaurantStore.restaurant.id!,
            orderId
          );
        } catch (err) {
          console.error('Error fetching order', err);
        }
      }
    } else {
      order = orderOrId;
    }

    if (!order) {
      ui.showToast(AlertType.Error, 'Pedido no encontrado');
      return false;
    }

    if (!modals) {
      throw new Error('Modals not setup');
    }

    if (isPosCall) store.removeUnseenOrder(order.id);

    modals.show(
      // @ts-ignore
      ModalPosViewOrder,
      {
        order: order,
      },
      {
        name: 'pos-view-order',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 800,
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 840,
        transition: 'none',
      }
    );
    return true;
  }

  function editOrder(order: IOrder, startingMode?: 'edit' | 'view') {
    if (isPosV2Active.value) {
      // The old modal defaults to view when no startingMode is passed.
      const mode = startingMode === 'edit' ? 'edit' : 'view';
      openOrderInPosV2Tab(order.id, mode)
        .then((opened) => {
          // Close the view modal (e.g. opened from Pedidos) once the order
          // page took over; hide() no-ops when the modal is not open.
          if (opened) modals.hide('pos-view-order');
        })
        .catch((err) => {
          console.error('Error opening order in POS V2 tab', err);
        });
      return;
    }

    if (!modals) {
      throw new Error('Modals not setup');
    }

    store.removeUnseenOrder(order.id);
    modals.show(
      ModalPosNewOrder,
      {
        order,
        startingMode,
      },
      {
        name: 'pos-new-order',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 800,
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 1280,
        transition: 'none',
        clickToClose: false,
      }
    );
  }

  /*
  
  const updating = ref(false);
  async function setStatus(status: OrderStatus) {
    if (updating.value) return;

    try {
      updating.value = true;

      const res = await OrdersService.setStatus(
        order.value!.id,
        status,
        restaurantStore.caja?.id
      );
    } catch (error) {
      ui.serverError(error, 'Error al actualizar el estado del pedido');
    } finally {
      updating.value = false;
    }
  }
  */

  async function refreshOrder(params: {
    order?: IOrder;
    restaurantId?: number;
    orderId?: number;
  }) {
    const order: IOrder | undefined = params.order;
    const restaurantId = order?.restaurantId || params.restaurantId;
    const orderId = order?.id || params.orderId;

    if (!restaurantId || !orderId) {
      throw new Error(
        'Missing required parameters for refreshOrder: restaurantId and orderId are required'
      );
    }

    const updatedOrder = await OrdersService.v2GetOrderById(
      restaurantId,
      orderId
    );
    const didUpdate = store.updateOrder(orderId, updatedOrder);

    // Orders outside the pending set (e.g. reopened archived orders) are not
    // in the store, so updateOrder ignores them — refresh the caller's
    // instance in place instead (provide/inject consumers re-render). The
    // absence check is required: updateOrder also returns false on its
    // stale-updatedAt guard, where params.order is typically the live store
    // instance and an assign would overwrite a newer WS payload.
    if (
      !didUpdate &&
      params.order &&
      !store.orders?.some((o) => o.id === orderId)
    ) {
      Object.assign(params.order, updatedOrder);
    }

    return updatedOrder;
  }

  async function setOrderStatus(
    order: IOrder,
    status: OrderStatus
  ): Promise<boolean> {
    try {
      const paidGuardError = getPaidOrderCancelDeclineGuardError({
        order,
        status,
      });
      if (paidGuardError) {
        ui.showToast(AlertType.Error, paidGuardError);
        return false;
      }

      if (status === OrderStatus.Progress) {
        const guardError = getOnlineAcceptCajaGuardError({
          order,
          caja: restaurantStore.caja,
          effectiveTier: restaurantStore.effectiveTier,
          branchSettingUseCajas:
            restaurantStore.selectedBranch?.settingUseCajas,
        });

        if (guardError) {
          ui.showToast(AlertType.Error, guardError);
          return false;
        }
      }

      await OrdersService.setStatus(order.id, status, restaurantStore.caja?.id);
      return true;
    } catch (error) {
      refreshOrder({ order }).catch((err) => {
        console.error('Error refreshing order', err);
      });
      // The client-side caja guard is a heuristic (it can miss caja-less club
      // money once order.cajaId got stamped by another payment); the server's
      // authoritative re-check answers with this code. Same UX as the guard:
      // toast + "not updated", so the user picks a caja and retries.
      if (getServerErrorMessage(error) === 'CONFLICT:CAJA_REQUIRED') {
        ui.showToast(AlertType.Error, 'Seleccione una caja');
        return false;
      }
      throw error;
    }
  }

  return {
    openOrder,
    editOrder,
    setOrderStatus,
    refreshOrder,
  };
}
